export interface DnfStats {
    dnfCount: number;
    totalRaces: number;
    /** dnfCount / totalRaces — always consistent with the two numbers above. */
    ratio: number;
}

interface FormatStatLike {
    finishedRaces?: number | null;
}

/** Above this share of DNF, a runner is flagged as risky in the UI. */
export const DNF_HIGH_THRESHOLD = 0.2;

/**
 * Rebuilds the DNF count and the total number of races from the backend's `dnfRatio`.
 *
 * The backend only persists `dnfRatio` (a 0..1 fraction rounded to 2 decimals), not the
 * raw counts, so we invert it against the number of finished races:
 *   ratio = dnf / (dnf + finished)  =>  dnf = ratio * finished / (1 - ratio)
 *
 * Approximate by design: `formatStats` only counts finishes in the four known formats
 * (`buildFormatStats` in backend/src/services/athlete.service.ts filters on the RaceFormat
 * enum), so races in other UTMB categories are missing from the denominator — we can show
 * 3/18 where the truth is 3/20. The rounding itself is harmless: the inversion is exact for
 * any ratio <= 0.3.
 *
 * Single source of truth for every DNF display so the numbers stay consistent across the
 * app. TODO: persist `dnfCount` / `totalRaces` on the athlete and drop this reconstruction
 * (tracked in plans/CODE_QUALITY_FIXES.md § 7.1 — local, /plans is gitignored).
 *
 * Returns `null` when the stats can't be derived (no ratio, no formatStats, or ratio >= 1).
 */
export function computeDnfStats(
    dnfRatio: number | null | undefined,
    formatStats: ReadonlyArray<FormatStatLike | null> | null | undefined,
): DnfStats | null {
    if (dnfRatio == null || dnfRatio < 0 || dnfRatio >= 1 || !formatStats) return null;

    const finishedRaces = formatStats.reduce((sum, s) => sum + (s?.finishedRaces ?? 0), 0);
    const dnfCount = Math.round((dnfRatio * finishedRaces) / (1 - dnfRatio) + 0.0001);
    const totalRaces = finishedRaces + dnfCount;

    return { dnfCount, totalRaces, ratio: totalRaces > 0 ? dnfCount / totalRaces : 0 };
}
